import axios from "../../../../api/axios";

import { CreateProfessionalJob, CreateTalentJob } from "../../types/createJobSchema";
import { Bucket, JobModeEnum, JobStatusEnum } from "../../enum/jobEnums";

import {
  CreateJobResponse,
  HireJobResponse,
  GetJobDetailsResponse,
  TopMatchedApplicant,
  GetJobCandidatesResponse,
  JobsSummaryResponse,
  TalentHeatmapResponse,
} from "../types/job.types";

import { formatProfessionalJobPayload, formatTalentJobPayload } from "../formatters/job.formatter";

export const createJobProfessional = async (payload: CreateProfessionalJob): Promise<CreateJobResponse> => {
  const formattedPayload = formatProfessionalJobPayload(payload);

  const response = await axios.post("/hire-jobs", formattedPayload);
  return response.data.data;
};

export const createJobTalent = async (payload: CreateTalentJob): Promise<CreateJobResponse> => {
  const formattedPayload = formatTalentJobPayload(payload);

  const response = await axios.post("/hire-jobs", formattedPayload);
  return response.data.data;
};

export const getJobDetails = async (id: number | string): Promise<GetJobDetailsResponse> => {
  const response = await axios.get(`/hire-jobs/${id}`);
  return response.data;
};

export interface GetHireJobsFilters {
  createdFrom?: string;
  createdTo?: string;
  applyEndsFrom?: string;
  applyEndsTo?: string;
  status?: JobStatusEnum;
  mode?: JobModeEnum;
  q?: string;
  countryIds?: string;
  offset?: number;
  limit?: number;
}

export interface GetJobsSummaryFilters {
  from?: string;
  to?: string;
}

export const getJobsSummary = async (filters?: GetJobsSummaryFilters): Promise<JobsSummaryResponse["data"]> => {
  const params = new URLSearchParams();

  if (filters?.from) {
    params.append("from", filters.from);
  }
  if (filters?.to) {
    params.append("to", filters.to);
  }

  const queryString = params.toString();
  const url = queryString ? `/hire-jobs/summary?${queryString}` : "/hire-jobs/summary";

  const response = await axios.get(url);
  return response.data.data;
};

export const getHireJobs = async (filters?: GetHireJobsFilters): Promise<HireJobResponse> => {
  const params = new URLSearchParams();

  if (filters?.createdFrom) {
    params.append("createdFrom", filters.createdFrom);
  }
  if (filters?.createdTo) {
    params.append("createdTo", filters.createdTo);
  }
  if (filters?.applyEndsFrom) {
    params.append("applyEndsFrom", filters.applyEndsFrom);
  }
  if (filters?.applyEndsTo) {
    params.append("applyEndsTo", filters.applyEndsTo);
  }
  if (filters?.status) {
    params.append("status", filters.status);
  }
  if (filters?.mode) {
    params.append("mode", filters.mode);
  }
  if (filters?.q) {
    params.append("q", filters.q);
  }
  if (filters?.countryIds) {
    params.append("countryIds", filters.countryIds);
  }
  if (filters?.offset !== undefined) {
    params.append("offset", filters.offset.toString());
  }
  if (filters?.limit !== undefined) {
    params.append("limit", filters.limit.toString());
  }

  const queryString = params.toString();
  const url = queryString ? `/hire-jobs/mine?${queryString}` : "/hire-jobs";
  // const url = queryString ? `/hire-jobs/mine?${queryString}` : "/hire-jobs/mine";

  const response = await axios.get(url);
  return response.data;
};

export const updateJobStatus = async (jobId: number, status: JobStatusEnum) => {
  const response = await axios.put(`/hire-jobs/${jobId}/status`, { status });
  return response.data;
};

export const getTopMatchedApplicants = async (jobId: number | string): Promise<TopMatchedApplicant[]> => {
  const response = await axios.get(`/hire-jobs/${jobId}/top-best-matched-applicants`);
  return response.data.data;
};

export interface GetJobCandidatesFilters {
  q?: string;
  bucket?: Bucket;
  offset?: number;
  limit?: number;
}

export const getJobCandidates = async (
  jobId: number | string,
  filters?: GetJobCandidatesFilters,
): Promise<GetJobCandidatesResponse> => {
  const params = new URLSearchParams();

  if (filters?.q) {
    params.append("name", filters.q);
  }
  if (filters?.bucket) {
    params.append("bucket", filters.bucket);
  }
  if (filters?.offset !== undefined) {
    params.append("offset", filters.offset.toString());
  }
  if (filters?.limit !== undefined) {
    params.append("limit", filters.limit.toString());
  }

  const queryString = params.toString();
  const url = queryString ? `/hire-jobs/${jobId}/candidates?${queryString}` : `/hire-jobs/${jobId}/candidates`;

  const response = await axios.get(url);
  return response.data;
};

export const getTalentHeatmap = async (): Promise<TalentHeatmapResponse["data"]> => {
  const response = await axios.get("/hire-jobs/analytics/talent-heatmap");
  return response.data.data;
};
